import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { PhoneVerificationDialogResponse } from '@box-shared/components/phone-verification-dialog/phone-verification-dialog.types';
import { AccountContactService } from './account-contact.service';
import { MatDialogRef } from '@angular/material/dialog';
import { BoxConfirmDialogComponent, ProfileDeletionDialogComponent } from '@box-shared/components';
import { ConfirmDialogResponse, SeoOptions } from '@box-types';
import { DialogService, SEOService } from '@box-core/services';
import { ProfileDeletionDialogResponseResponse } from '@box-shared/components/profile-deletion-dialog/profile-deletion-dialog-data.types';
import { deleteJsonLd } from '@box/utils';
import { mobilePhoneValidator } from '@box-shared/validators';
import { ActivatedRoute, Router } from '@angular/router';

@Component({
  selector: 'account-contact',
  templateUrl: './account-contact.page.html',
  styleUrls: ['./account-contact.page.scss'],
  providers: [AccountContactService],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AccountContactPage implements OnInit, OnDestroy {
  public mobileControl: FormControl;
  public verifiedMSISDN: string;
  public mobilePhones: string[];

  constructor(
    private accountContactService: AccountContactService,
    private changeDetectorRef: ChangeDetectorRef,
    private dialogService: DialogService,
    private activatedRoute: ActivatedRoute,
    private seoService: SEOService,
    private router: Router
  ) {}

  public ngOnInit(): void {
    this.verifiedMSISDN = this.accountContactService.getVerifiedMSISDN();
    this.mobilePhones = this.accountContactService.getMobilePhoneNumbers();
    this.mobileControl = new FormControl(
      this.verifiedMSISDN,
      Validators.compose([Validators.required, mobilePhoneValidator()]) // eslint-disable-line @typescript-eslint/unbound-method
    );
    this.setMetaTags();
    this.changeDetectorRef.detectChanges();
  }

  ngOnDestroy(): void {
    deleteJsonLd();
  }

  public onSubmit(): void {
    if (this.mobileControl.invalid) return;
    const mobilePhone = this.mobileControl.value as string;
    this.verifyPhone(mobilePhone);
  }

  public onPhoneSelect(mobilePhone: string): void {
    this.updatePhoneInputField(mobilePhone);
  }
  public selectPhone(mobilePhones: string[], phone: string): void {
    mobilePhones.forEach((option) => (option = phone));
    this.mobileControl.setValue(phone);
  }
  private updatePhoneInputField(mobilePhone: string): void {
    this.verifiedMSISDN = mobilePhone;
    this.mobileControl.setValue(mobilePhone);
    this.mobileControl.markAsUntouched();
    this.changeDetectorRef.detectChanges();
  }

  private verifyPhone(mobilePhone: string): void {
    this.accountContactService
      .openPhoneVerificationDialog(mobilePhone)
      .afterClosed()
      .subscribe((response: PhoneVerificationDialogResponse) => {
        if (!response?.success || !response?.verifyResponse) return;
        this.accountContactService.updateUserDetails(response.verifyResponse);
        this.updatePhoneInputField(this.accountContactService.getVerifiedMSISDN());
      });
  }

  public initiateDeletionFlow(): void {
    this.showDeletionConfirmationDialog()
      .afterClosed()
      .subscribe((response: ConfirmDialogResponse) => {
        if (!response?.accepted) return;
        this.dialogService
          .openDialog(ProfileDeletionDialogComponent, {
            ariaLabel: 'delete_confirmation'
          })
          .afterClosed()
          .subscribe((response: ProfileDeletionDialogResponseResponse) => {
            if (response?.success) this.accountContactService.onProfileDeletion();
          });
      });
  }

  private showDeletionConfirmationDialog(): MatDialogRef<BoxConfirmDialogComponent> {
    return this.dialogService.openConfirmDialog({
      title: 'delete_profile',
      messages: ['delete_profile_dialog_text_1', 'delete_profile_dialog_text_2', 'delete_profile_dialog_text_3'],
      cancelText: 'back_',
      confirmText: 'delete_'
    });
  }

  private setMetaTags(): void {
    const options: SeoOptions = { title: this.activatedRoute.snapshot.data.breadcrumbName, url: this.router.url };
    this.seoService.setTags(options);
  }
}
